import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Loader2,
  AlertCircle,
  Bot,
  CornerLeftUp,
  FolderKanban,
  Workflow,
} from "lucide-react";
import { Virtuoso, GroupedVirtuoso } from "react-virtuoso";
import { Link } from "wouter";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchField } from "@/components/search-field";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn, type AccessTokenGetter } from "@/lib/utils";
import {
  DEFAULT_SESSION_TITLE,
  groupByDate,
  isProjectTaskSession,
  isMcpSession,
  isWorkflowSession,
  type Session,
  type ChildSessionSummary,
  workflowSessionDisplayTitle,
} from "@/lib/session";
import type { Agent } from "@/hooks/use-agents";
import { SESSIONS_PAGE_SIZE } from "@/hooks/use-sessions";
import { useNotificationContext } from "@/components/notification-provider";
import { NotificationIndicator } from "@/components/notification-indicator";
import { useDraftSessionIds } from "@/hooks/use-draft-session-ids";
import { useIsMobile } from "@/hooks/use-media-query";
import { MobileQuickLaunchButton } from "@/components/mobile-chat-header-buttons";
import { AgentDropdown } from "@/components/agent-dropdown";
import { ProjectSidebarSection } from "@/components/project-sidebar-section";
import { AgentSidebarSection } from "@/components/agent-sidebar-section";
import {
  useSidebarPinOrdering,
  type SidebarPinOrderingApi,
} from "@/hooks/use-sidebar-pin-ordering";
import { McpSessionIndicator, SessionRow } from "@/components/session-row";
import type { SessionNotificationState } from "@/hooks/use-notifications";
import type { Project, TaskStatus } from "@/lib/project-types";
import { RealtimeVoiceButton } from "@/components/realtime-voice-button";
import { canUseRealtimeVoice } from "@/lib/tenant-config";
import { NotificationInbox } from "@/components/notification-inbox";
import { SettingsMenu, type SettingsMenuProps } from "@/components/settings-menu";

export { groupByDate };

const EMPTY_PENDING_SESSION_IDS: ReadonlySet<string> = new Set();

const layerIxLogo = "/logo-layerix.svg";

const LAYER_IX_LOGO_MASK = {
  WebkitMaskImage: `url("${layerIxLogo}")`,
  maskImage: `url("${layerIxLogo}")`,
  WebkitMaskPosition: "center",
  maskPosition: "center",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskSize: "contain",
  maskSize: "contain",
} satisfies CSSProperties;

/* ------------------------------------------------------------------ */
/*  Rename Dialog (extracted to isolate keystroke re-renders)         */
/* ------------------------------------------------------------------ */

interface RenameDialogProps {
  /** The session being renamed, or null when the dialog is closed. */
  session: Session | null;
  onConfirm: (id: string, title: string) => void;
  onCancel: () => void;
}

/**
 * Self-contained rename dialog with its own local input state.
 *
 * Previously the rename input's state lived in `SessionSidebar`, so every
 * keystroke re-rendered the entire session list (every row, tooltip, badge,
 * and notification indicator).  Extracting the dialog isolates re-renders
 * to just this component — typing stays smooth even with hundreds of
 * sessions in the sidebar.
 */
const RenameDialog = ({ session, onConfirm, onCancel }: RenameDialogProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");

  // Sync local state when the target session changes (i.e. when the dialog
  // opens for a different session).  Using an effect with a ref guard avoids
  // resetting the value on every re-render of the dialog itself.
  const lastSessionId = useRef<string | null>(null);
  useEffect(() => {
    if (session && session.id !== lastSessionId.current) {
      // When the session still has the default placeholder title, start with
      // an empty input so the user can type a real name immediately instead
      // of first having to delete "New Conversation".
      setValue(session.title === DEFAULT_SESSION_TITLE ? "" : session.title);
      lastSessionId.current = session.id;
    }
    if (!session) {
      lastSessionId.current = null;
    }
  }, [session]);

  const open = session !== null;

  const handleConfirm = useCallback(() => {
    if (session && value.trim()) {
      onConfirm(session.id, value.trim());
    }
    onCancel();
  }, [session, value, onConfirm, onCancel]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("sidebar.renameDialogTitle")}</DialogTitle>
          <DialogDescription>{t("sidebar.renameDialogDesc")}</DialogDescription>
        </DialogHeader>
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("sidebar.renamePlaceholder")}
          className="mt-2"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleConfirm();
          }}
          // Auto-focus the input when the dialog opens so the user can
          // start typing immediately without an extra click.
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t("common:actions.cancel")}</Button>
          <Button onClick={handleConfirm}>{t("common:actions.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/*  SessionRowWithChildren (recursive SessionRow tree)                 */
/* ------------------------------------------------------------------ */

type SessionLike = Session | ChildSessionSummary;

interface ThreadSessionIndicatorProps {
  sessionId: string;
  label: string;
  className: string;
}

function ThreadSessionIndicator({
  sessionId,
  label,
  className,
}: ThreadSessionIndicatorProps) {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-testid={`thread-session-indicator-${sessionId}`}
      className={cn(
        "inline-flex shrink-0 items-center justify-center text-muted-foreground",
        className,
      )}
    >
      <CornerLeftUp aria-hidden="true" className="size-3.5" />
    </span>
  );
}

interface WorkflowSessionIndicatorProps {
  sessionId: string;
  className: string;
}

function WorkflowSessionIndicator({
  sessionId,
  className,
}: WorkflowSessionIndicatorProps) {
  const { t } = useTranslation("chat");
  const label = t("workflowView.workflow");
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-testid={`workflow-session-icon-${sessionId}`}
      className={cn(
        "inline-flex shrink-0 items-center justify-center text-muted-foreground",
        className,
      )}
    >
      <Workflow aria-hidden="true" className="size-3.5" />
    </span>
  );
}

interface SessionRowWithChildrenProps {
  session: SessionLike;
  activeSessionId: string | null;
  notifMap: ReadonlyMap<string, SessionNotificationState>;
  pendingSessionIds: ReadonlySet<string>;
  draftSessionIds: ReadonlySet<string>;
  getTaskStatusForSession?: (sessionId: string) => TaskStatus | null | undefined;
  agentMap: ReadonlyMap<string, Agent>;
  isMobile: boolean;
  onSelect: (id: string) => void;
  onRenameOpen: (id: string, title: string) => void;
  renameLabel: string;
  renameConversationLabel: string;
  expandedChildren: ReadonlySet<string>;
  onToggleExpand: (id: string) => void;
  childSessionsMap: ReadonlyMap<string, ChildSessionSummary[]>;
  childLoadingMap: ReadonlyMap<string, boolean>;
  childCursorMap: ReadonlyMap<string, string | null>;
  onLoadMoreChildren: (id: string) => void;
  loadMoreChildrenLabel: string;
  expandChildrenLabel: string;
  collapseChildrenLabel: string;
  openThreadLabel: string;
  depth?: number;
}

/**
 * Wraps a SessionRow with an expand/collapse chevron and renders the same
 * component recursively for child sessions when expanded.
 *
 * The chevron appears when child metadata, loaded children, or expansion
 * state indicates that the session can be expanded.
 */
const SessionRowWithChildren = memo(({
  session,
  activeSessionId,
  notifMap,
  pendingSessionIds,
  draftSessionIds,
  getTaskStatusForSession,
  agentMap,
  isMobile,
  onSelect,
  onRenameOpen,
  renameLabel,
  renameConversationLabel,
  expandedChildren,
  onToggleExpand,
  childSessionsMap,
  childLoadingMap,
  childCursorMap,
  onLoadMoreChildren,
  loadMoreChildrenLabel,
  expandChildrenLabel,
  collapseChildrenLabel,
  openThreadLabel,
  depth = 0,
}: SessionRowWithChildrenProps) => {
  const threadsEnabled = true;
  const childSessions = childSessionsMap.get(session.id) ?? [];
  const childLoading = childLoadingMap.get(session.id) ?? false;
  const isActive = session.id === activeSessionId;
  const isExpanded = expandedChildren.has(session.id);
  const hasChildren = session.has_visible_children || childSessions.length > 0 || isExpanded;
  const showChevron = threadsEnabled && hasChildren;
  const isThread = session.session_type === "thread" || Boolean(session.parent_session_id);
  const agent = agentMap.get(session.agent_id ?? "");
  const sessionForRow = session as Session;
  const isTaskSession = "metadata" in session && isProjectTaskSession(sessionForRow);
  const isWorkflow = isWorkflowSession(session);
  const displayTitle = workflowSessionDisplayTitle(sessionForRow.title);

  return (
    <div>
      <div className="flex items-center">
        {showChevron && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(session.id);
            }}
            className="ml-1 inline-flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            aria-label={isExpanded ? collapseChildrenLabel : expandChildrenLabel}
            data-testid={depth > 0 ? "child-session-expand-chevron" : "session-expand-chevron"}
          >
            <ChevronDown
              className={cn(
                "size-3.5 transition-transform",
                isExpanded ? "rotate-0" : "-rotate-90"
              )}
            />
          </button>
        )}
        {isThread && (
          <ThreadSessionIndicator
            sessionId={session.id}
            label={openThreadLabel}
            className={showChevron ? "size-4" : "ml-1 size-5"}
          />
        )}
        {isWorkflow && (
          <WorkflowSessionIndicator
            sessionId={session.id}
            className={showChevron || isThread ? "size-4" : "ml-1 size-5"}
          />
        )}
        <div className="min-w-0 flex-1">
          <SessionRow
            session={sessionForRow}
            displayTitle={displayTitle}
            isActive={isActive}
            agentName={agent?.name}
            agentType={agent?.type as "agent" | "team" | undefined}
            notifState={notifMap.get(session.id) ?? null}
            isPending={pendingSessionIds.has(session.id)}
            hasDraft={draftSessionIds.has(session.id)}
            isTaskSession={isTaskSession}
            taskStatus={isTaskSession ? getTaskStatusForSession?.(session.id) : undefined}
            isMobile={isMobile}
            onSelect={onSelect}
            onRenameOpen={onRenameOpen}
            renameLabel={renameLabel}
            renameConversationLabel={renameConversationLabel}
          />
        </div>
      </div>
      {isExpanded && threadsEnabled && (
        <div className="ml-5 border-l border-muted pb-1 pl-3">
          {childLoading && childSessions.length === 0 ? (
            <div className="flex justify-center py-2">
              <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
            </div>
          ) : childSessions.length > 0 ? (
            <>
              {childSessions.map((child) => (
                <SessionRowWithChildren
                  key={child.id}
                  session={child}
                  activeSessionId={activeSessionId}
                  notifMap={notifMap}
                  pendingSessionIds={pendingSessionIds}
                  draftSessionIds={draftSessionIds}
                  getTaskStatusForSession={getTaskStatusForSession}
                  agentMap={agentMap}
                  isMobile={isMobile}
                  onSelect={onSelect}
                  onRenameOpen={onRenameOpen}
                  renameLabel={renameLabel}
                  renameConversationLabel={renameConversationLabel}
                  expandedChildren={expandedChildren}
                  onToggleExpand={onToggleExpand}
                  childSessionsMap={childSessionsMap}
                  childLoadingMap={childLoadingMap}
                  childCursorMap={childCursorMap}
                  onLoadMoreChildren={onLoadMoreChildren}
                  loadMoreChildrenLabel={loadMoreChildrenLabel}
                  expandChildrenLabel={expandChildrenLabel}
                  collapseChildrenLabel={collapseChildrenLabel}
                  openThreadLabel={openThreadLabel}
                  depth={depth + 1}
                />
              ))}
              {childCursorMap.get(session.id) && (
                <button type="button" disabled={childLoading} className="px-2 py-1 text-xs text-muted-foreground hover:underline"
                  onClick={() => onLoadMoreChildren(session.id)}>
                  {childLoading ? <Loader2 className="size-3.5 animate-spin" /> : loadMoreChildrenLabel}
                </button>
              )}
            </>
          ) : (
            <div className="py-1 text-xs text-muted-foreground">
              {/* No threads yet */}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
SessionRowWithChildren.displayName = "SessionRowWithChildren";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface SessionSidebarProps {
  sessions: Session[];
  cacheState?: "none" | "cached-revalidating" | "stale" | "live";
  agentsCacheState?: "none" | "cached-revalidating" | "stale" | "live";
  activeSessionId: string | null;
  loading: boolean;
  error: string | null;
  onRetry?: () => void | Promise<void>;
  agents: Agent[];
  onSelect: (id: string) => void;
  onCreate: () => void;
  /** Agent of the open chat; the primary desktop plus starts another chat with it. */
  newChatAgentId?: string | null;
  /** Desktop chat shell controls. Omitted in the mobile drawer and embedded sidebars. */
  settingsMenuProps?: SettingsMenuProps;
  onRename: (id: string, title: string) => void;
  /** Server-side search query (owned by useSessions hook). */
  searchQuery: string;
  onSearchChange: (q: string) => void;
  /** Opens the global conversation and message search palette. */
  onOpenAdvancedSearch?: () => void;
  /** True while a search request is in flight. */
  searching: boolean;
  /** True when there are older sessions not yet loaded. */
  hasMore: boolean;
  /** True while loading the next page. */
  loadingMore: boolean;
  /** A replacement interrupted pagination; keep a visible path to retry it. */
  canRetryLoadMore?: boolean;
  /** Called by Virtuoso's endReached to load the next page. */
  onLoadMore: () => void;
  /** Retries a deferred/failed load-more operation from its preserved cursor. */
  onRetryLoadMore?: () => void | Promise<void>;
  /** Opens a project folder or an existing task's board chat. */
  onOpenProject?: (projectId: string, taskId?: string) => void;
  /** Creates and opens a project task chat directly from a folder's plus button. */
  onCreateProjectTaskChat?: (project: Project) => void | Promise<void>;
  activeProjectId?: string | null;
  /** Sessions with a local voice-note upload/transcription in progress. */
  pendingSessionIds?: ReadonlySet<string>;
  /** Returns a cached task status for task-bound session sidebar rows. */
  getTaskStatusForSession?: (sessionId: string) => TaskStatus | null | undefined;
  className?: string;
  /**
   * Called when the user clicks the collapse button.  When provided (mobile
   * drawer), the sidebar is hidden entirely by the parent instead of
   * shrinking to the icon-rail collapsed state.  When omitted (desktop),
   * the sidebar falls back to its internal collapsed state.
   */
  onCollapse?: () => void;
  /** Opens quick launch from the mobile drawer header. */
  onQuickLaunch?: () => void;
  /** Access token getter for child session API calls. */
  getToken?: AccessTokenGetter;
  /** Verified tenant from /api/me, used only for cosmetic feature gating. */
  tenant?: string;
  /** Refreshes history after a server-owned Realtime recording is created. */
  onRealtimeRecordingCreated?: () => void | Promise<void>;
  /** Refreshes history after a server-owned Realtime recording closes. */
  onRealtimeRecordingClosed?: () => void | Promise<void>;
  /** Registers child session IDs with the parent session store. */
  registerKnownSessionIds?: (ids: string[]) => void;
  /** Signals that a parent's child sessions changed and should be reloaded. */
  childRefreshTrigger?: { parentSessionId: string; nonce: number } | null;
  /** Refreshes the flat session list when a child session changes. */
  onSessionListRefresh?: () => void | Promise<void>;
  /** Raw server-ordered pins; inaccessible IDs are filtered by the section. */
  agentPins?: string[];
  agentPinsLoading?: boolean;
  agentPinsError?: string | null;
  /** RBAC-filtered agent catalog state used by the pinned Agents section. */
  agentsLoading?: boolean;
  agentsError?: string | null;
  defaultPinInitializing?: boolean;
  /** Agent selected by the routed conversations hub, if any. */
  activeAgentId?: string | null;
  onOpenAgent?: (agentId: string) => void;
  onCreateAgentConversation?: (agentId: string) => void | Promise<void>;
  onRefreshAgentPins?: () => void | Promise<void>;
  onRefreshAgents?: () => void | Promise<void>;
  /** Shell-owned pin ordering also drives root-path agent landing. */
  pinOrdering?: SidebarPinOrderingApi;
  /** Config-marked agent IDs promoted above saved user pin order. */
  pinnedByDefaultAgentIds?: readonly string[];
}

export function SessionSidebar({
  sessions,
  cacheState = "none",
  agentsCacheState = "none",
  activeSessionId,
  loading,
  error,
  onRetry,
  agents,
  onSelect,
  onCreate,
  newChatAgentId,
  settingsMenuProps,
  onRename,
  searchQuery,
  onSearchChange,
  onOpenAdvancedSearch,
  searching,
  hasMore,
  loadingMore,
  canRetryLoadMore = false,
  onLoadMore,
  onRetryLoadMore,
  onOpenProject,
  onCreateProjectTaskChat,
  activeProjectId,
  pendingSessionIds = EMPTY_PENDING_SESSION_IDS,
  getTaskStatusForSession,
  className,
  onCollapse,
  onQuickLaunch,
  getToken,
  tenant,
  onRealtimeRecordingCreated,
  onRealtimeRecordingClosed,
  registerKnownSessionIds,
  childRefreshTrigger,
  onSessionListRefresh,
  agentPins,
  agentPinsLoading = false,
  agentPinsError = null,
  agentsLoading = false,
  agentsError = null,
  defaultPinInitializing = false,
  activeAgentId,
  onOpenAgent,
  onCreateAgentConversation,
  onRefreshAgentPins,
  onRefreshAgents,
  pinOrdering,
  pinnedByDefaultAgentIds,
}: SessionSidebarProps) {
  const { t, i18n } = useTranslation();
  // ChatPage supplies the shell-owned instance so root landing and both
  // sidebar renderers agree. Retain the local owner for narrow embeds and
  // existing direct consumers that do not pass one.
  const fallbackPinOrdering = useSidebarPinOrdering(
    pinOrdering ? null : getToken ?? null,
  );
  const effectivePinOrdering = pinOrdering ?? fallbackPinOrdering;
  const agentMap = useMemo(() => {
    const map = new Map<string, Agent>();
    for (const a of agents) map.set(a.id, a);
    return map;
  }, [agents]);
  const individualAgents = useMemo(
    () => agents.filter((agent) => agent.type === "agent"),
    [agents],
  );
  const newChatAgent = individualAgents.find((agent) => agent.id === newChatAgentId);
  const createWithActiveAgent = () => {
    if (newChatAgent && onCreateAgentConversation) void onCreateAgentConversation(newChatAgent.id);
    else onCreate();
  };
  const newChatTooltip = newChatAgent
    ? t("sidebar.newChatWithAgent", { agent: newChatAgent.name })
    : t("sidebar.newChat");
  const { notificationState } = useNotificationContext();
  const notifMap = useMemo(() => notificationState, [notificationState]);
  const draftSessionIds = useDraftSessionIds();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [renameSession, setRenameSession] = useState<Session | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // ── Child session expansion state ────────────────────────────────────────
  const [expandedChildren, setExpandedChildren] = useState<Set<string>>(new Set());
  const [childSessionsMap, setChildSessionsMap] = useState<Map<string, ChildSessionSummary[]>>(new Map());
  const [childLoadingMap, setChildLoadingMap] = useState<Map<string, boolean>>(new Map());
  const [childCursorMap, setChildCursorMap] = useState<Map<string, string | null>>(new Map());
  const registerKnownSessionIdsRef = useRef(registerKnownSessionIds);

  useEffect(() => {
    registerKnownSessionIdsRef.current = registerKnownSessionIds;
  }, [registerKnownSessionIds]);

  const threadsEnabled = true;
  const isSearchMode = !!searchQuery.trim();

  // The lazy child tree and the flat root list have independent data sources.
  // Refresh the root list as well so a newly created thread is immediately
  // grouped by its own last_active_at. Older servers ignore include_threads
  // and simply return the existing top-level-only shape.
  useEffect(() => {
    if (!childRefreshTrigger) return;
    void onSessionListRefresh?.();
  }, [childRefreshTrigger, onSessionListRefresh]);

  // Thread creation happens in ChatInterface, while this component owns the
  // lazy-loaded child-session cache. Preload the affected parent's children
  // so a manual expansion is instant, but leave the tree collapsed until the
  // user clicks its chevron.
  useEffect(() => {
    if (!childRefreshTrigger || !getToken) return;

    const { parentSessionId } = childRefreshTrigger;
    let cancelled = false;

    setChildLoadingMap((prev) => new Map(prev).set(parentSessionId, true));

    void (async () => {
      try {
        const params = new URLSearchParams();
        params.set("types", "thread");
        params.set("limit", "50");
        const url = `/api/sessions/${parentSessionId}/children?${params}`;
        const { fetchWithAuth } = await import("@/lib/utils");
        const res = await fetchWithAuth(url, { headers: {} }, getToken);
        if (!res.ok || cancelled) return;

        const data = await res.json();
        if (cancelled) return;
        const childSessions: ChildSessionSummary[] = data.items ?? [];
        setChildSessionsMap((prev) => {
          const existing = prev.get(parentSessionId) ?? [];
          if (existing.length === 0) return new Map(prev).set(parentSessionId, childSessions);
          const fresh = new Map(childSessions.map((child) => [child.id, child]));
          const held = existing.map((child) => fresh.get(child.id) ?? child);
          const seen = new Set(held.map((child) => child.id));
          return new Map(prev).set(parentSessionId, [...held, ...childSessions.filter((child) => !seen.has(child.id))]);
        });
        setChildCursorMap((prev) => new Map(prev).set(parentSessionId, data.next_cursor ?? null));
        registerKnownSessionIdsRef.current?.(childSessions.map((item) => item.id));
      } catch {
        // Keep the last known children visible if a background refresh fails.
      } finally {
        if (!cancelled) {
          setChildLoadingMap((prev) => {
            const next = new Map(prev);
            next.delete(parentSessionId);
            return next;
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [childRefreshTrigger, getToken]);

  const handleToggleExpand = useCallback(async (sessionId: string) => {
    setExpandedChildren((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });

    // Lazy-load children if not already loaded
    if (!childSessionsMap.has(sessionId) && getToken) {
      setChildLoadingMap((prev) => new Map(prev).set(sessionId, true));
      try {
        const params = new URLSearchParams();
        params.set("types", "thread");
        params.set("limit", "50");
        const url = `/api/sessions/${sessionId}/children?${params}`;
        const { fetchWithAuth } = await import("@/lib/utils");
        const res = await fetchWithAuth(url, { headers: {} }, getToken);
        if (res.ok) {
          const data = await res.json();
          const childSessions: ChildSessionSummary[] = data.items ?? [];
          setChildSessionsMap((prev) => new Map(prev).set(sessionId, childSessions));
          setChildCursorMap((prev) => new Map(prev).set(sessionId, data.next_cursor ?? null));
          registerKnownSessionIds?.(childSessions.map((item) => item.id));
        }
      } catch {
        // Silently fail — expansion is non-critical
        setChildSessionsMap((prev) => new Map(prev).set(sessionId, []));
      } finally {
        setChildLoadingMap((prev) => {
          const next = new Map(prev);
          next.delete(sessionId);
          return next;
        });
      }
    }
  }, [childSessionsMap, getToken, registerKnownSessionIds]);

  const handleLoadMoreChildren = useCallback(async (sessionId: string) => {
    const cursor = childCursorMap.get(sessionId);
    if (!cursor || !getToken || childLoadingMap.get(sessionId)) return;
    setChildLoadingMap((prev) => new Map(prev).set(sessionId, true));
    try {
      const { fetchWithAuth } = await import("@/lib/utils");
      const params = new URLSearchParams({ types: "thread", limit: "50", before: cursor });
      const res = await fetchWithAuth(`/api/sessions/${sessionId}/children?${params}`, { headers: {} }, getToken);
      if (!res.ok) return;
      const data = await res.json();
      if (!Array.isArray(data.items)) return;
      setChildSessionsMap((prev) => {
        const existing = prev.get(sessionId) ?? [];
        const seen = new Set(existing.map((item) => item.id));
        return new Map(prev).set(sessionId, [...existing, ...data.items.filter((item: ChildSessionSummary) => !seen.has(item.id))]);
      });
      setChildCursorMap((prev) => new Map(prev).set(sessionId, data.next_cursor ?? null));
      registerKnownSessionIds?.(data.items.map((item: ChildSessionSummary) => item.id));
    } finally {
      setChildLoadingMap((prev) => { const next = new Map(prev); next.delete(sessionId); return next; });
    }
  }, [childCursorMap, childLoadingMap, getToken, registerKnownSessionIds]);

  // The sessions array is already server-filtered (search) and server-paginated.
  // Date grouping derives groupCounts for Virtuoso's grouped mode.
  const grouped = useMemo(
    () => groupByDate(sessions, t("sidebar.today"), t("sidebar.yesterday"), i18n.language),
    [sessions, t, i18n.language],
  );

  // Flat, group-ordered array of sessions.  ``renderItem`` indexes into this
  // by the flat item index GroupedVirtuoso provides; the collapsed sidebar
  // (non-grouped) passes it straight to Virtuoso's ``data`` prop.
  const flatSessions = useMemo(
    () => grouped.flatMap((g) => g.items),
    [grouped],
  );

  const groupCounts = useMemo(
    () => grouped.map((g) => g.items.length),
    [grouped],
  );

  // Reset the search field and return focus to it so the user can keep
  // typing without an extra click. Standard clear-button UX pattern.
  const handleSearchClear = useCallback(() => {
    onSearchChange("");
    searchInputRef.current?.focus();
  }, [onSearchChange]);

  const handleRenameOpen = useCallback((id: string, title: string) => {
    setRenameSession({ id, title } as Session);
  }, []);

  const handleRenameConfirm = useCallback((id: string, title: string) => {
    onRename(id, title);
    setRenameSession(null);
  }, [onRename]);

  const handleRenameCancel = useCallback(() => {
    setRenameSession(null);
  }, []);

  // Stable callbacks for Virtuoso's endReached — avoids re-subscribing
  // the scroll listener on every render.
  const handleEndReached = useCallback(() => {
    if (hasMore && !loadingMore) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, onLoadMore]);

  // Grouped list rendering uses ``GroupedVirtuoso`` (not the base ``Virtuoso``
  // with ``data`` + ``groupCounts``, which silently drops items and misaligns
  // the item passed to ``itemContent``).  GroupedVirtuoso calls this with the
  // flat item index across all groups, so we look the session up in
  // ``flatSessions`` ourselves.
  const renderItem = useCallback(
    (index: number) => {
      const session = flatSessions[index];
      if (!session) return null;

      // When threads are enabled and not in search mode, render with
      // expand/collapse and child rows.  In search mode, render flat.
      if (threadsEnabled && !isSearchMode) {
        return (
          <div className="px-2">
            <SessionRowWithChildren
              session={session}
              activeSessionId={activeSessionId}
              notifMap={notifMap}
              pendingSessionIds={pendingSessionIds}
              draftSessionIds={draftSessionIds}
              getTaskStatusForSession={getTaskStatusForSession}
              agentMap={agentMap}
              isMobile={isMobile}
              onSelect={onSelect}
              onRenameOpen={handleRenameOpen}
              renameLabel={t("sidebar.rename")}
              renameConversationLabel={t("sidebar.renameConversation")}
              expandedChildren={expandedChildren}
              onToggleExpand={handleToggleExpand}
              childSessionsMap={childSessionsMap}
              childLoadingMap={childLoadingMap}
              childCursorMap={childCursorMap}
              onLoadMoreChildren={handleLoadMoreChildren}
              loadMoreChildrenLabel={t("threads.loadMoreChildren")}
              expandChildrenLabel={t("threads.expandChildren")}
              collapseChildrenLabel={t("threads.collapseChildren")}
              openThreadLabel={t("threads.openThread")}
            />
          </div>
        );
      }

      // Search mode or threads disabled: flat rendering (original behavior).
      const isThread = session.session_type === "thread" || Boolean(session.parent_session_id);
      const isWorkflow = isWorkflowSession(session);
      const displayTitle = workflowSessionDisplayTitle(session.title);
      return (
        <div className="px-2">
          <div className="flex items-center">
            {isThread && (
              <ThreadSessionIndicator
                sessionId={session.id}
                label={t("threads.openThread")}
                className="ml-1 size-5"
              />
            )}
            {isWorkflow && (
              <WorkflowSessionIndicator
                sessionId={session.id}
                className={isThread ? "size-4" : "ml-1 size-5"}
              />
            )}
            <div className="min-w-0 flex-1">
              <SessionRow
                session={session}
                displayTitle={displayTitle}
                isActive={session.id === activeSessionId}
                agentName={agentMap.get(session.agent_id ?? "")?.name}
                agentType={agentMap.get(session.agent_id ?? "")?.type as "agent" | "team"}
                notifState={notifMap.get(session.id) ?? null}
                isPending={pendingSessionIds.has(session.id)}
                hasDraft={draftSessionIds.has(session.id)}
                isTaskSession={isProjectTaskSession(session)}
                taskStatus={isProjectTaskSession(session) ? getTaskStatusForSession?.(session.id) : undefined}
                isMobile={isMobile}
                onSelect={onSelect}
                onRenameOpen={handleRenameOpen}
                renameLabel={t("sidebar.rename")}
                renameConversationLabel={t("sidebar.renameConversation")}
              />
            </div>
          </div>
        </div>
      );
    },
    [flatSessions, activeSessionId, agentMap, notifMap, pendingSessionIds, draftSessionIds, getTaskStatusForSession, isMobile, onSelect, handleRenameOpen, t, threadsEnabled, isSearchMode, expandedChildren, handleToggleExpand, childSessionsMap, childLoadingMap, childCursorMap, handleLoadMoreChildren],
  );

  const renderGroup = useCallback(
    (groupIndex: number) => (
      <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
        {grouped[groupIndex]?.label}
      </div>
    ),
    [grouped],
  );

  const Footer = useCallback(
    () => (
      loadingMore ? (
        <div className="flex justify-center py-3">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      ) : canRetryLoadMore && onRetryLoadMore ? (
        <div className="flex justify-center py-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => { void onRetryLoadMore(); }}
          >
            {t("common:actions.retry")}
          </Button>
        </div>
      ) : !collapsed && isSearchMode && !hasMore && flatSessions.length % SESSIONS_PAGE_SIZE !== 0 && onOpenAdvancedSearch ? (
        <div className="px-2 py-1">
          <button
            type="button"
            aria-label={t("sidebar.openAdvancedSearch")}
            onClick={onOpenAdvancedSearch}
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <Search className="size-4 shrink-0" aria-hidden="true" />
            {t("sidebar.openAdvancedSearch")}
          </button>
        </div>
      ) : null
    ),
    [canRetryLoadMore, collapsed, flatSessions.length, hasMore, isSearchMode, loadingMore, onOpenAdvancedSearch, onRetryLoadMore, t],
  );

  /* -------------------------------------------------------------- */
  /*  Collapsed state                                                */
  /* -------------------------------------------------------------- */

  if (collapsed) {
    return (
      <div className={cn("flex h-full w-16 flex-col border-r bg-background", className)}>
        <div className="flex items-center justify-center border-b p-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t("sidebar.expandSidebar")} onClick={() => setCollapsed(false)}>
                  <ChevronRight className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{t("sidebar.expandSidebar")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex flex-1 flex-col items-center gap-2 py-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={createWithActiveAgent} aria-label={newChatTooltip}>
                  <Plus className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{newChatTooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {settingsMenuProps && !isMobile && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild><span><NotificationInbox /></span></TooltipTrigger>
                <TooltipContent side="right">{t("notifications.inbox.title")}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild><span><SettingsMenu {...settingsMenuProps} /></span></TooltipTrigger>
                <TooltipContent side="right">{t("common:header.settings")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {getToken && onOpenProject && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    asChild
                    variant="ghost"
                    size="icon"
                    aria-label={t("projects:viewAllProjects")}
                  >
                    <Link href="/projects/list">
                      <FolderKanban className="size-4" />
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">{t("projects:viewAllProjects")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {getToken && onOpenAgent && (
            <AgentDropdown
              agents={individualAgents}
              currentAgentId={null}
              loading={agentsLoading}
              error={agentsError}
              getToken={getToken}
              onSelect={onOpenAgent}
              onCreateSession={onCreateAgentConversation}
              ariaLabel={t("sidebar.openAgents")}
              trigger={(
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  data-testid="sidebar-agents-view-collapsed"
                  aria-label={t("sidebar.openAgents")}
                  title={t("sidebar.openAgents")}
                >
                  <Bot className="size-4" />
                </Button>
              )}
            />
          )}
          <Virtuoso
            className="h-full min-h-0 overflow-x-hidden"
            data={flatSessions}
            increaseViewportBy={400}
            endReached={handleEndReached}
            itemContent={(_index, session) => (
              <div className="flex justify-center py-1">
                <TooltipProvider key={session.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={session.id === activeSessionId ? "secondary" : "ghost"}
                        size="icon"
                        onClick={() => onSelect(session.id)}
                      >
                        <span className="relative">
                          <NotificationIndicator
                            state={notifMap.get(session.id) ?? null}
                            statusOverride={pendingSessionIds.has(session.id) ? "pending" : undefined}
                            hasDraft={draftSessionIds.has(session.id)}
                            taskSession={isProjectTaskSession(session)}
                            taskStatus={isProjectTaskSession(session) ? getTaskStatusForSession?.(session.id) : undefined}
                            testId={isProjectTaskSession(session) ? `task-session-icon-${session.id}` : undefined}
                            idleIcon={isMcpSession(session) ? <McpSessionIndicator sessionId={session.id} /> : undefined}
                          />
                          {isWorkflowSession(session) && (
                            <WorkflowSessionIndicator
                              sessionId={session.id}
                              className="absolute -bottom-1 -right-1 size-3.5 rounded-full bg-background"
                            />
                          )}
                        </span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {workflowSessionDisplayTitle(session.title)}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
            components={{ Footer }}
          />
        </div>
      </div>
    );
  }

  /* -------------------------------------------------------------- */
  /*  Expanded state                                                 */
  /* -------------------------------------------------------------- */

  return (
    <div className={cn("flex h-full w-72 flex-col border-r bg-background", className)}>
      {/* Header */}
      <div className="flex min-h-[53px] items-center justify-between border-b px-3 py-2.5">
        <h2 className={cn("group -m-1 flex min-w-0 items-center p-1 text-foreground", !isMobile && "relative z-10 w-7 shrink-0")}>
          <span className="sr-only">Layer Nine</span>
          <span
            aria-hidden="true"
            className={cn(
              "block h-7 w-[3.5rem] max-w-5 shrink-0 overflow-hidden transition-[max-width] duration-200 ease-out group-hover:max-w-[3.5rem] motion-reduce:transition-none",
              isMobile ? "relative" : "pointer-events-none absolute left-1 top-1/2 z-10 -translate-y-1/2 bg-background",
            )}
          >
            <span className="absolute left-0 top-1/2 size-5 -translate-y-1/2">
              <img
                src="/icon-512-src.png"
                alt=""
                className="block size-5 origin-center transition-opacity duration-200 ease-out group-hover:animate-[spin_200ms_linear_infinite] group-hover:opacity-0 motion-reduce:group-hover:animate-none motion-reduce:transition-none dark:invert"
              />
            </span>
            <span
              className="absolute right-0 top-0 h-7 w-[3.5rem] -translate-x-2 bg-current opacity-0 transition-[opacity,transform] duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100 motion-reduce:transition-none"
              style={LAYER_IX_LOGO_MASK}
            />
          </span>
        </h2>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-0.5">
          {/* On mobile (touch), skip the Tooltip wrapper — Radix Tooltip
              intercepts the first tap to open the tooltip, so the onClick
              (create conversation) only fires on the second tap. Rendering
              the bare Button makes the first tap create a conversation
              immediately. */}
          {isMobile ? (
            <Button variant="ghost" size="icon" className="size-7" onClick={onCreate}>
              <Plus className="size-4" />
            </Button>
          ) : (
            <AgentDropdown
              agents={individualAgents}
              currentAgentId={null}
              loading={agentsLoading}
              error={agentsError}
              getToken={getToken}
              onSelect={(agentId) => void onCreateAgentConversation?.(agentId)}
              onCreateSession={onCreateAgentConversation}
              ariaLabel={t("sidebar.chooseNewChatAgent")}
              trigger={(
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0"
                  aria-label={t("sidebar.chooseNewChatAgent")}
                  title={t("sidebar.chooseNewChatAgent")}
                >
                  <Plus className="size-4" />
                </Button>
              )}
            />
          )}
          {settingsMenuProps && !isMobile && <><NotificationInbox /><SettingsMenu {...settingsMenuProps} /></>}
          {!isMobile && getToken && canUseRealtimeVoice(tenant) && (
            <div className="shrink-0">
              <RealtimeVoiceButton
                tenant={tenant}
                getToken={getToken}
                compact
                onRecordingCreated={onRealtimeRecordingCreated}
                onRecordingClosed={onRealtimeRecordingClosed}
              />
            </div>
          )}
          {isMobile && getToken && (
            <RealtimeVoiceButton
              tenant={tenant}
              getToken={getToken}
              onRecordingCreated={onRealtimeRecordingCreated}
              onRecordingClosed={onRealtimeRecordingClosed}
            />
          )}
          {isMobile && onQuickLaunch ? (
            // Mobile drawers close through the backdrop or header menu, so an
            // icon-rail collapse control is not useful here.
            <MobileQuickLaunchButton onQuickLaunch={onQuickLaunch} />
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="size-7 shrink-0" aria-label={t("sidebar.collapseSidebar")} onClick={() => onCollapse?.() ?? setCollapsed(true)}>
                    <ChevronLeft className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("sidebar.collapseSidebar")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      <div className="px-3 py-2">
        <SearchField
          ref={searchInputRef}
          value={searchQuery}
          onChange={onSearchChange}
          placeholder={t("sidebar.searchPlaceholder")}
          ariaLabel={t("sidebar.searchAria")}
          clearLabel={t("sidebar.clearSearch")}
          onClear={handleSearchClear}
          clearTestId="sidebar-search-clear"
          leadingIcon={onOpenAdvancedSearch ? <Search className="size-4" aria-hidden="true" /> : undefined}
          leadingAction={onOpenAdvancedSearch ? { label: t("sidebar.openAdvancedSearch"), onClick: onOpenAdvancedSearch } : undefined}
          searching={searching}
          searchingLabel={t("sidebar.searching")}
        />
      </div>

      {getToken && onOpenProject && (
        <ProjectSidebarSection
          getToken={getToken}
          onOpenProject={onOpenProject}
          onCreateProjectTaskChat={onCreateProjectTaskChat}
          activeProjectId={activeProjectId}
          pinOrdering={effectivePinOrdering}
        />
      )}

      {error && (
        <div className="mx-3 mb-2 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="size-4 shrink-0" />
          <span className="min-w-0 flex-1 break-words">{error}</span>
          {onRetry && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 px-2 text-xs"
              onClick={() => { void onRetry(); }}
            >
              {t("common:actions.retry")}
            </Button>
          )}
        </div>
      )}

      {/* Loading spinner (initial load only) */}
      {loading && sessions.length === 0 && (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Session list — virtualized via react-virtuoso in grouped mode.
          Groups correspond to date headers (Today, Yesterday, …).
          endReached fires when the user scrolls near the bottom, triggering
          loadMore() to fetch the next page of older sessions.

          ``min-h-0`` is essential: inside a flex-col parent, a flex-1 child
          without min-h-0 gets min-height:auto, which makes it grow to fit
          content rather than constrain to the available space — Virtuoso
          would then render at 0 visible height because the parent already
          consumed all the flex space with the header/search/error bars. */}
      {!loading && flatSessions.length > 0 && (
        <div className="min-h-0 flex-1" ref={(node) => {
          if (!node) return;
          if (cacheState === "cached-revalidating" || cacheState === "stale") node.setAttribute("inert", "");
          else node.removeAttribute("inert");
        }}>
          <GroupedVirtuoso
            className="h-full overflow-x-hidden"
            groupCounts={groupCounts}
            groupContent={renderGroup}
            itemContent={renderItem}
            increaseViewportBy={400}
            endReached={handleEndReached}
            components={{ Footer }}
          />
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && flatSessions.length === 0 && (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-2 py-8 text-center text-xs text-muted-foreground">
          <span>{searchQuery
            ? t("sidebar.noSearchResults")
            : t("common:errors.noConversations")}</span>
          {searchQuery && onOpenAdvancedSearch && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={t("sidebar.openAdvancedSearch")}
              onClick={onOpenAdvancedSearch}
              className="h-auto max-w-full min-h-8 whitespace-normal py-1.5"
            >
              <Search aria-hidden="true" />
              {t("sidebar.openAdvancedSearch")}
            </Button>
          )}
        </div>
      )}

      {getToken
        && agentPins !== undefined
        && onOpenAgent
        && onCreateAgentConversation && (
        <div ref={(node) => {
          if (!node) return;
          if (agentsCacheState === "cached-revalidating" || agentsCacheState === "stale") node.setAttribute("inert", "");
          else node.removeAttribute("inert");
        }}>
        <AgentSidebarSection
          agents={agents}
          pins={agentPins}
          pinsLoading={agentPinsLoading}
          pinsError={agentPinsError}
          agentsLoading={agentsLoading}
          agentsError={agentsError}
          defaultPinInitializing={defaultPinInitializing}
          getToken={getToken}
          activeAgentId={activeAgentId}
          pinnedByDefaultAgentIds={pinnedByDefaultAgentIds}
          pinOrdering={effectivePinOrdering}
          onOpenAgent={onOpenAgent}
          onCreateConversation={onCreateAgentConversation}
          onRetry={onRefreshAgentPins}
          onRetryAgents={onRefreshAgents}
        />
        </div>
      )}

      {/* Rename dialog — extracted to its own component so typing in the
          input only re-renders the dialog, not the entire session list. */}
      <RenameDialog
        session={renameSession}
        onConfirm={handleRenameConfirm}
        onCancel={handleRenameCancel}
      />

    </div>
  );
}
